import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { walletAPI, hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Wallet, Loader2, Save, Landmark, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useEntitlements } from '../context/EntitlementsContext';
import { UpgradeGate } from '../components/UpgradeGate';

const bs = (n) => `Bs. ${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const emptyForm = {
  bank_code: '', account_type: 'corriente', account_number: '',
  holder_name: '', holder_doc_type: 'V', holder_doc_number: '', phone: '',
};

export default function WalletPage() {
  const { selectedChurchId, canUpdate } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [wallet, setWallet] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const { hasModule, loading: entitlementsLoading } = useEntitlements();
  const walletEnabled = hasModule('wallet_dispersion');

  const fetchWallet = useCallback(() => {
    if (!walletEnabled) { setLoading(false); return; }
    setLoading(true);
    walletAPI.get(selectedChurchId)
      .then(res => {
        const data = res.data?.data || null;
        setWallet(data);
        const d = data?.dispersion || {};
        setForm({
          bank_code: d.bank_code || '', account_type: d.account_type || 'corriente',
          account_number: d.account_number || '', holder_name: d.holder_name || '',
          holder_doc_type: d.holder_doc_type || 'V', holder_doc_number: d.holder_doc_number || '',
          phone: d.phone || '',
        });
      })
      .catch(err => {
        if (err?.response?.status === 404) setUnavailable(true);
        else toast.error(hubError(err, 'Error al cargar el wallet'));
      })
      .finally(() => setLoading(false));
  }, [selectedChurchId, walletEnabled]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await walletAPI.updateDispersion(form, selectedChurchId);
      toast.success('Cuenta de dispersión actualizada');
      fetchWallet();
    } catch (err) { toast.error(hubError(err, 'Error al guardar la cuenta')); } finally { setSaving(false); }
  };

  return (
    <div>
      <TopBar title="Wallet de la iglesia" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      {!entitlementsLoading && !walletEnabled ? (
        <UpgradeGate
          title="Wallet y dispersión"
          description="El wallet de la congregación y la cuenta de dispersión son un módulo adicional."
          benefits={[
            'Saldo de la congregación en Bs. y su equivalente en USD',
            'Cuenta bancaria de dispersión para retirar los aportes',
            'Estado de la conexión con la pasarela de pagos',
          ]}
        />
      ) : (
      <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-3xl">
        {loading || entitlementsLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : unavailable ? (
          <div className="empty-state" data-testid="wallet-unavailable">
            <Wallet className="w-12 h-12 text-muted-foreground/40" />
            <p className="font-medium">Wallet aún no disponible</p>
            <p className="text-sm text-muted-foreground max-w-md">
              El servidor de Tu Iglesia App todavía no expone el wallet de la iglesia en este entorno.
              Cuando se publique, verás aquí el saldo y la cuenta de dispersión.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="stat-card">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-xl bg-[#056A84]/10 flex items-center justify-center mb-3">
                    <Wallet className="w-5 h-5 text-[#056A84]" />
                  </div>
                  <p className="text-2xl font-semibold text-[#056A84]" data-testid="wallet-balance-ves">{bs(wallet?.balance_ves)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Saldo disponible</p>
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-xl bg-[#1F9D7A]/10 flex items-center justify-center mb-3">
                    <ShieldCheck className="w-5 h-5 text-[#1F9D7A]" />
                  </div>
                  <p className="text-2xl font-semibold text-[#1F9D7A]" data-testid="wallet-balance-usd">${Number(wallet?.balance_usd || 0).toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Equivalente USD</p>
                  <div className="flex gap-2 mt-3">
                    <Badge variant={wallet?.caurapay_configured ? 'default' : 'secondary'} className="text-[10px]">
                      {wallet?.caurapay_configured ? 'CauraPay activo' : 'CauraPay sin configurar'}
                    </Badge>
                    {wallet?.caura_api_key_last4 && <Badge variant="outline" className="text-[10px]">····{wallet.caura_api_key_last4}</Badge>}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><Landmark className="w-5 h-5" /> Cuenta de dispersión</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Código del banco</Label><Input value={form.bank_code} onChange={e => setForm({...form, bank_code: e.target.value})} placeholder="0134" data-testid="wallet-bank-input" /></div>
                  <div className="space-y-2">
                    <Label>Tipo de cuenta</Label>
                    <Select value={form.account_type} onValueChange={v => setForm({...form, account_type: v})}>
                      <SelectTrigger data-testid="wallet-account-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="corriente">Corriente</SelectItem>
                        <SelectItem value="ahorro">Ahorro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Número de cuenta</Label><Input value={form.account_number} onChange={e => setForm({...form, account_number: e.target.value})} data-testid="wallet-account-input" /></div>
                <div className="space-y-2"><Label>Titular</Label><Input value={form.holder_name} onChange={e => setForm({...form, holder_name: e.target.value})} data-testid="wallet-holder-input" /></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de documento</Label>
                    <Select value={form.holder_doc_type} onValueChange={v => setForm({...form, holder_doc_type: v})}>
                      <SelectTrigger data-testid="wallet-doc-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="V">V</SelectItem>
                        <SelectItem value="E">E</SelectItem>
                        <SelectItem value="J">J</SelectItem>
                        <SelectItem value="G">G</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Documento</Label><Input value={form.holder_doc_number} onChange={e => setForm({...form, holder_doc_number: e.target.value})} data-testid="wallet-doc-input" /></div>
                  <div className="space-y-2"><Label>Teléfono</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="wallet-phone-input" /></div>
                </div>
                {canUpdate('wallet') && (
                  <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={handleSave} disabled={saving} data-testid="wallet-save-button">
                    {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Guardar cuenta
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
